/**
 * DriverSupportDrawer — slide-up support sheet for drivers.
 *
 * Visual treatment mirrors AccountDrawer / EarningsHubDrawer (cream
 * surface, white cards, spring transition). Static for now — the
 * primary CTAs surface contact options without requiring a live agent
 * integration. Future-ready: the `Chat with Support` row can drop in
 * an Intercom / Crisp / live agent panel.
 */
import React from "react";
import { X, MessageCircle, AlertOctagon, ClipboardList, User, Car, CreditCard, ChevronRight, ExternalLink } from "lucide-react";

import { Sheet } from "@/shared/motion";

const ISSUE_TYPES = [
  { key: "block",   label: "Block Issue",       Icon: ClipboardList, body: "Something went wrong with a block you claimed or completed." },
  { key: "rider",   label: "Rider Issue",       Icon: User,          body: "Report a safety, conduct, or no-show issue with a rider." },
  { key: "vehicle", label: "Vehicle Issue",     Icon: Car,           body: "Mechanical or vehicle-related issue affecting your work." },
  { key: "payment", label: "Payment Question",  Icon: CreditCard,    body: "Earnings, payouts, or adjustments." },
];

export default function DriverSupportDrawer({ open, onClose }) {
  const supportMailto = "mailto:drivers@hermesmobility.com?subject=Driver%20Support%20-%20Link";

  return (
    <Sheet
      open={open}
      onClose={onClose}
      className="bg-[#F9F7F4]"
      maxHeight="88vh"
      testid="driver-support-drawer"
      ariaLabel="Driver support"
    >
        <div className="flex items-center justify-center pt-3 pb-1">
          <span className="w-10 h-1 rounded-full bg-[#0A1128]/15" />
        </div>
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-4 w-9 h-9 rounded-full bg-white border border-black/[0.06] shadow-sm inline-flex items-center justify-center text-[#0A1128] hover:bg-[#F1ECE3] link-pressable"
          aria-label="Close support"
          data-testid="driver-support-close"
        >
          <X size={16} strokeWidth={1.8} />
        </button>
        <div className="overflow-y-auto px-5 sm:px-6 pb-[max(env(safe-area-inset-bottom),32px)]" style={{ maxHeight: "calc(88vh - 24px)" }}>
          <div className="space-y-4 pt-3">
            <header className="px-1" data-testid="driver-support-header">
              <div className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[#ed6500]">Driver Support</div>
              <h1 className="font-serif font-light text-[26px] leading-tight text-[#0A1128] mt-0.5">How can we help?</h1>
              <p className="text-[12.5px] text-[#515E7B] mt-1 leading-relaxed">
                Reach our driver team directly, or pick an issue category below.
              </p>
            </header>

            {/* Primary: Chat with Support */}
            <a
              href={supportMailto}
              className="rounded-3xl bg-[#0A1128] text-white px-5 py-4 flex items-center gap-3 hover:bg-[#101a37] transition-colors"
              data-testid="driver-support-chat"
            >
              <span className="w-10 h-10 rounded-full bg-white/10 inline-flex items-center justify-center"><MessageCircle size={16} strokeWidth={1.9}/></span>
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.22em] uppercase text-white/55">Chat with Support</div>
                <div className="text-[14px] mt-0.5">drivers@hermesmobility.com</div>
              </div>
              <ExternalLink size={14} className="text-white/55" />
            </a>

            {/* Issue categories */}
            <div className="space-y-2" data-testid="driver-support-issues">
              <div className="px-1 text-[10px] tracking-[0.28em] uppercase font-semibold text-[#515E7B]/85">Report Issue</div>
              {ISSUE_TYPES.map(({ key, label, Icon, body }) => (
                <a
                  key={key}
                  href={`${supportMailto.replace("subject=Driver%20Support%20-%20Link", `subject=Driver%20Support%20-%20${encodeURIComponent(label)}`)}`}
                  className="rounded-3xl bg-white border border-black/[0.06] shadow-sm px-5 py-4 flex items-center gap-3 hover:border-[#ed6500] transition-colors"
                  data-testid={`driver-support-issue-${key}`}
                >
                  <span className="w-10 h-10 rounded-full bg-[#F1ECE3] text-[#0A1128] inline-flex items-center justify-center"><Icon size={16} strokeWidth={1.9}/></span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-medium text-[#0A1128]">{label}</div>
                    <div className="text-[11.5px] text-[#515E7B] mt-0.5 truncate">{body}</div>
                  </div>
                  <ChevronRight size={14} className="text-[#0A1128]/40" />
                </a>
              ))}
            </div>

            {/* Live agent placeholder */}
            <div className="rounded-3xl border border-dashed bg-[#F1ECE3]/60 border-[#0A1128]/15 p-5" data-testid="driver-support-live-placeholder">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] mb-1">
                <AlertOctagon size={12}/> Live agent chat
              </div>
              <p className="text-[12.5px] text-[#515E7B] leading-relaxed">
                In-app live chat with Link Support is coming soon. For anything urgent, use the chat link above.
              </p>
            </div>
          </div>
        </div>
    </Sheet>
  );
}
